// Weekly resident nights. They are not rows in the schedule sheet (the sheet is for bookings),
// so they are generated here and merged into the event list. Shared by the site and
// /api/event (Node), so no browser APIs here.
//
// A late-night time (before 06:00) belongs to the night of the listed date: DJ Andrea on
// "Friday 2 October, 00:00" starts at midnight between Friday and Saturday.

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const pad = (n) => String(n).padStart(2, '0');

export const RESIDENTS = [
    {
        key: 'dj-andrea',
        title: 'DJ Andrea',
        weekdays: [5, 6], // Friday and Saturday nights
        time: '00:00',
        since: '2026-09-29', // first night listed on the site; earlier weekends are not generated
        genre: 'Resident DJ · Rock',
        poster: '/img/dj-andrea.jpg',
        about: {
            label: 'About the DJ',
            band_name: 'DJ Andrea',
            bio: [
                'When the clock strikes midnight on Fridays and Saturdays, Andrea Jónsdóttir takes over the decks at Dillon, as she has done almost every weekend for more than two decades. She is Dillon’s resident DJ and a true Reykjavík institution.',
                'Known across Iceland as the “rokkamma”, the grandmother of rock, Andrea has presented music on RÚV’s Rás 2 since 1984 and was awarded the Order of the Falcon in 2018 for her work championing Icelandic and international popular music.',
                'Expect rock the way it should be played: classics, deep cuts and the odd surprise, picked by someone who has heard it all and still loves it. Grab a whisky, find a spot near the booth and stay until closing.',
            ].join('\n\n'),
            based_in: 'Reykjavík',
            links: {},
        },
    },
];

/** Minutes after the start of the listed date; late-night times sort after the evening. */
export const nightMinutes = (time) => {
    const [h, m] = String(time || '21:00').split(':').map(Number);
    return ((h < 6 ? h + 24 : h) * 60) + (m || 0);
};

export const compareEvents = (a, b) =>
    (a.dateObj - b.dateObj) || (nightMinutes(a.time) - nightMinutes(b.time));

/**
 * Resident events for one year, from each resident's `since` date on. Dates where the
 * sheet already lists that resident are skipped so a manual row wins.
 * @param {number} year
 * @param {Array} sheetEvents - events parsed from that year's tab
 */
export const residentEventsForYear = (year, sheetEvents = []) => {
    const out = [];
    for (const r of RESIDENTS) {
        const listed = new Set(
            sheetEvents.filter((e) => e.title.toLowerCase().includes(r.title.toLowerCase())).map((e) => e.isoDate),
        );
        for (let d = new Date(year, 0, 1); d.getFullYear() === year; d.setDate(d.getDate() + 1)) {
            if (!r.weekdays.includes(d.getDay())) continue;
            const month = d.getMonth();
            const dayNum = d.getDate();
            const isoDate = `${year}-${pad(month + 1)}-${pad(dayNum)}`;
            if (isoDate < r.since || listed.has(isoDate)) continue;
            out.push({
                id: `res-${r.key}-${isoDate}`,
                slug: `${isoDate}-${r.key}`,
                isoDate,
                title: r.title,
                dateObj: new Date(year, month, dayNum),
                dateDisplay: `${dayNum}. ${MONTH_NAMES[month]}`,
                dayNum,
                time: r.time,
                dayOfWeek: DAY_NAMES[d.getDay()],
                genre: r.genre,
                poster: r.poster,
                tickets: '',
                entry: '',
                monthYear: `${MONTH_NAMES[month]} ${year}`,
                month,
                resident: r.key,
                about: r.about,
            });
        }
    }
    return out;
};

/** Sheet events of one year plus that year's resident nights, in running order. */
export const withResidents = (sheetEvents, year) =>
    [...sheetEvents, ...residentEventsForYear(year, sheetEvents)].sort(compareEvents);
